import { useEffect, useRef } from "react"
import {
  ANALYTIC_TYPES,
  ANALYTIC_TYPE_TO_EVENT,
  PLAYER_STATES,
  PLAYER_STATE_TO_EVENT,
  getViewMediaMessage,
} from "../utils/viewMediaPlayer"
import { createId } from "../utils/visitor"

// The branding intro runs ~3 s; every real video is far longer. Only used as
// a safety net if the player's own intro/main signal (type 7) is ever missed.
const INTRO_MAX_SECONDS = 10
// A jump back to the start only begins a new play once the current play got
// this far — earlier rewinds are part of the same play.
const REPLAY_AFTER_PROGRESS = 0.9
// "Back at the start" means within this many seconds of 0.
const RESTART_WINDOW_SECONDS = 3
// A playing stretch counts for no more than the time that actually passed
// (plus this much for message timing), so a seek never counts as watching.
const WATCH_SLACK_SECONDS = 1

const PHASES = Object.freeze({ IDLE: "idle", INTRO: "intro", MAIN: "main" })

// ViewMedica's own analytics payload sends percent as "50%", not 50 — strip
// anything that isn't part of the number before it reaches the API, which
// expects a clean number.
const toNumber = (value) => {
  if (value === undefined || value === null) return undefined
  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""))
  return Number.isNaN(parsed) ? undefined : parsed
}

/*
  Tracks one ViewMedica <iframe> from the messages the player posts on its own
  (see utils/viewMediaPlayer) — no polling and no calls into the player. Events
  go to the page's queue (usePlayerEventQueue), never straight to the API.

  Intro filtering. Each play runs as a playlist: a ~3 s branding intro, then
  the main video. The intro fires its own Video Started, percent and step
  analytics under the main video's label, so the label can't tell them apart.
  What can is "Playlist Item Reached" (type 7): its `item` is "Logo" for the
  intro and equals the `label` for the main video. Nothing is sent outside the
  main video. As observed on staging, one play arrives as:

    6 playlist started -> 7 "Logo" -> 2 -> state 1 (3 s) -> intro 3/4 ->
    state 9 -> state 0 -> 7 <label> -> state 1 (main, t 0) -> 4, 3, 4 ... ->
    3 100% -> state 9 -> state 2 -> 5 video end -> state 0 -> 8 playlist ended

  Play-throughs. Each play of the main video gets its own playId, so the
  admin side can count how many times a video was played. A play starts when
  the main video is reached (type 7, so every replay — the intro runs again),
  or on a jump back to the start once the current play passed 90%, or after
  it ended. Earlier rewinds stay in the same play.

  `enabled: false` (admin preview opens) no-ops entirely — no listener, no
  events sent.
*/
export const useVideoPlaybackTracking = ({
  iframeRef,
  videoId,
  source,
  trackingIds,
  enabled,
  enqueueEvent,
}) => {
  const enqueueEventRef = useRef(enqueueEvent)
  enqueueEventRef.current = enqueueEvent

  useEffect(() => {
    if (!enabled || !videoId || !iframeRef.current) return

    const iframe = iframeRef.current
    let phase = PHASES.IDLE
    // The current play, or null before the first one.
    let play = null
    let lastState = null

    const send = (eventType, extra = {}) => {
      enqueueEventRef.current?.({
        videoId,
        source,
        eventType,
        playId: play?.playId ?? null,
        ...trackingIds,
        ...extra,
      })
    }

    const openPlay = () => {
      play = {
        playId: createId(),
        ended: false,
        maxProgress: 0,
        milestones: new Set(),
        watchedSeconds: 0,
        // The open playing stretch: where it started and when.
        segment: null,
        // The first state of a play is always sent, even if it repeats the
        // previous one (the intro hands over while already "playing").
        stateSent: false,
      }
    }

    const recordProgress = (currentTime, duration) => {
      if (!play || !Number.isFinite(currentTime) || !(duration > 0)) return
      play.maxProgress = Math.max(play.maxProgress, currentTime / duration)
    }

    const closeSegment = (currentTime) => {
      const segment = play?.segment
      if (!segment) return
      play.segment = null
      if (!Number.isFinite(currentTime)) return

      const advanced = currentTime - segment.time
      const elapsed =
        (performance.now() - segment.startedAt) / 1000 + WATCH_SLACK_SECONDS
      if (advanced > 0) play.watchedSeconds += Math.min(advanced, elapsed)
    }

    // Only for a state that is "playing" while the main video is on screen.
    const shouldRestartPlay = (currentTime) => {
      if (!play) return true
      const atStart =
        currentTime === undefined || currentTime < RESTART_WINDOW_SECONDS
      if (!atStart) return false
      return play.ended || play.maxProgress >= REPLAY_AFTER_PROGRESS
    }

    const handleAnalytic = (analyticType, payload) => {
      switch (analyticType) {
        case ANALYTIC_TYPES.BROCHURE_PRINTED:
          // Printing the brochure isn't part of watching, so it has no play.
          send(ANALYTIC_TYPE_TO_EVENT[analyticType], {
            playId: null,
            label: payload.label,
          })
          return

        case ANALYTIC_TYPES.PLAYLIST_STARTED:
          phase = PHASES.INTRO
          return

        case ANALYTIC_TYPES.PLAYLIST_ITEM_REACHED:
          if (payload.item && payload.item === payload.label) {
            phase = PHASES.MAIN
            openPlay()
          } else {
            phase = PHASES.INTRO
          }
          return

        case ANALYTIC_TYPES.PLAYLIST_ENDED:
          phase = PHASES.IDLE
          if (play) play.ended = true
          return

        default:
          break
      }

      const eventType = ANALYTIC_TYPE_TO_EVENT[analyticType]
      if (!eventType || phase !== PHASES.MAIN || !play || play.ended) return

      const percent = toNumber(payload.percent)
      if (analyticType === ANALYTIC_TYPES.PERCENT_REACHED) {
        // The player can report the same milestone twice in one play.
        if (percent === undefined || play.milestones.has(percent)) return
        play.milestones.add(percent)
        play.maxProgress = Math.max(play.maxProgress, percent / 100)
      }

      send(eventType, {
        percent,
        // A step's title ("Introduction", "Symptoms", ...), not a numeric
        // index — sent through as-is, same as label.
        step: payload.step,
        label: payload.label,
      })

      // Only the main video sends Video End; it is what the admin side
      // counts as a finished play.
      if (analyticType === ANALYTIC_TYPES.VIDEO_ENDED) play.ended = true
    }

    const handleStateChange = (payload) => {
      const state = Number(payload.state)
      const currentTime = toNumber(payload.currentTime)
      const duration = toNumber(payload.duration)
      const isPlaying = state === PLAYER_STATES.PLAYING

      // Safety net for a missed type 7: the length gives the item away.
      if (duration > 0) {
        if (duration <= INTRO_MAX_SECONDS) {
          if (phase === PHASES.MAIN) phase = PHASES.INTRO
        } else if (isPlaying && phase !== PHASES.MAIN) {
          phase = PHASES.MAIN
          openPlay()
        }
      }

      if (phase !== PHASES.MAIN || !play) {
        lastState = state
        return
      }

      closeSegment(currentTime)
      recordProgress(currentTime, duration)
      if (isPlaying && play.stateSent && shouldRestartPlay(currentTime)) {
        openPlay()
      }
      if (isPlaying) {
        play.segment = {
          time: currentTime ?? 0,
          startedAt: performance.now(),
        }
      }

      const eventType = PLAYER_STATE_TO_EVENT[state]
      if (
        eventType &&
        !play.ended &&
        (state !== lastState || !play.stateSent)
      ) {
        send(eventType, {
          currentTime,
          duration,
          watchedSeconds: Math.round(play.watchedSeconds),
        })
        play.stateSent = true
      }

      lastState = state
    }

    const handleMessage = (event) => {
      const data = getViewMediaMessage(event, iframe)
      if (!data) return

      if (data.analyticType !== undefined) {
        handleAnalytic(Number(data.analyticType), data.payload || {})
      } else if (data.event === "onStateChange") {
        handleStateChange(data.payload || {})
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [enabled, videoId, source, trackingIds, iframeRef])
}
